import { TransactionStatusEnum } from "@bitwarden/web-vault/app/models/enum/transactionType";

export type PlaidBootstrapAction = "recovery" | "sync" | "skip";

type VaultPlaidAccountRef = { id?: string };

type VaultTransactionRef = {
  accountLink?: { id?: string | null };
  definition?: string;
};

/**
 * Vault Plaid account with no imported (non-pending) transactions — partial/interrupted import.
 * Mirrors {@link fiskilVaultAccountsMissingImportedTransactions}.
 */
export const plaidVaultAccountsMissingImportedTransactions = (
  vaultPlaidAccounts: VaultPlaidAccountRef[] | null | undefined,
  transactions: VaultTransactionRef[] | null | undefined,
): boolean => {
  const accounts = vaultPlaidAccounts ?? [];
  const txs = transactions ?? [];
  return accounts.some((account) => {
    if (!account.id) {
      return false;
    }
    const hasImported = txs.some(
      (t) => t.accountLink?.id === account.id && t.definition === TransactionStatusEnum.transaction,
    );
    return !hasImported;
  });
};

/**
 * Bootstrap / session recovery decision for Plaid (parallel to Fiskil, never altering it).
 *
 * Decision inputs:
 *  - `serverItemCount`       — linked Plaid items on the backend (`getItems()`); `null` when unavailable.
 *  - vault Plaid accounts.
 *  - whether any vault Plaid account is missing imported transactions.
 *  - `hasUnsavedSyncedData`  — server flag: transactions were fetched but import-complete was never
 *    called (cursor not committed). True means a prior session was interrupted mid-save.
 *
 * Returns:
 *  - `recovery` — finish/redo the import: server has unsaved data, vault accounts missing transactions,
 *     or the server reports linked items but the vault has no Plaid accounts.
 *  - `sync` — vault already has Plaid accounts with data → incremental delta sync.
 *  - `skip` — nothing to do (no items, no vault accounts).
 */
export const resolvePlaidBootstrapAction = (
  serverItemCount: number | null,
  vaultPlaidAccounts: VaultPlaidAccountRef[],
  options?: {
    vaultAccountsMissingTransactions?: boolean;
    hasUnsavedSyncedData?: boolean;
    accountsSynced?: boolean;
  },
): PlaidBootstrapAction => {
  // Server knows a prior session fetched data but never committed the cursor — recover it.
  // Guard on serverItemCount > 0: if the server has no items, has_unsaved_synced_data being
  // true is a stale/inconsistent server state and should not trigger a spurious recovery import.
  if (options?.hasUnsavedSyncedData && (serverItemCount ?? 0) > 0) {
    return "recovery";
  }

  // Only treat missing transactions as a broken import if the server has NOT confirmed a clean
  // save. accounts_synced=true means the import completed; a zero-transaction account is
  // legitimate (new account, no history) and should not re-trigger recovery.
  if (options?.vaultAccountsMissingTransactions && !options?.accountsSynced) {
    return "recovery";
  }

  if ((serverItemCount ?? 0) > 0 && vaultPlaidAccounts.length === 0) {
    return "recovery";
  }

  if (vaultPlaidAccounts.length > 0) {
    return "sync";
  }

  return "skip";
};
